import React from 'react';

/**
 * The "ask to watch" affordance on a locked speech.
 *
 * Extracted from SpeechCard purely to keep that file under the 200-line
 * component budget — it has no state of its own. The parent owns the request
 * call and feeds the outcome back as `state`.
 *
 * @param {string}   speakerId    - member_id to send the request to
 * @param {string}   speakerName  - display name, for the button copy
 * @param {boolean}  isProvisional- speaker has never signed in (cannot accept)
 * @param {'none'|'sent'|'pending'|'error'} state
 * @param {Function} onRequest    - (speakerId) => void
 * @param {boolean}  stopClick    - true inside a <Link>: swallow the click so
 *                                  asking doesn't also navigate to the speech
 * @param {boolean}  prominent    - rendered over a darkened poster frame rather
 *                                  than on a card surface: bigger, primary fill,
 *                                  and light-on-dark text that must NOT use the
 *                                  theme tokens (the scrim is dark in both themes)
 */
export default function LockedSpeechCTA({
  speakerId, speakerName, isProvisional = false, state = 'none', onRequest,
  stopClick = false, prominent = false,
}) {
  const firstName = speakerName ? speakerName.split(' ')[0] : 'them';

  // On a dark scrim the normal text tokens are unreadable in light theme, so
  // these are deliberately fixed light values — same exception the card's
  // existing overlay badges already take.
  const onScrim = {
    background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.22)', color: 'rgba(255,255,255,0.92)',
  };

  // Nobody is behind a provisional id, so a request would sit pending forever.
  if (isProvisional) {
    if (prominent) {
      return (
        <p className="text-xs font-semibold px-3 py-1.5 rounded-lg" style={onScrim}>
          {speakerName || 'This speaker'} hasn&apos;t joined yet
        </p>
      );
    }
    return (
      <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
        {speakerName || 'This speaker'} hasn&apos;t joined yet
      </p>
    );
  }

  if (state === 'sent') {
    if (prominent) {
      return (
        <p className="text-xs font-bold px-3 py-2 rounded-lg" style={onScrim}>
          ✓ Request sent
        </p>
      );
    }
    return (
      <p
        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg inline-block"
        style={{
          background: 'var(--color-success-dim)',
          border: '1px solid var(--color-success-border)',
          color: 'var(--color-success)',
        }}
      >
        Request sent
      </p>
    );
  }

  if (state === 'pending') {
    if (prominent) {
      return (
        <p className="text-xs font-semibold px-3 py-2 rounded-lg" style={onScrim}>
          Waiting for {firstName} to accept
        </p>
      );
    }
    return (
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Waiting for {firstName} to accept
      </p>
    );
  }

  if (state === 'error') {
    if (prominent) {
      return (
        <p className="text-xs font-semibold px-3 py-2 rounded-lg"
           style={{ ...onScrim, color: 'var(--color-danger)' }}>
          Couldn&apos;t send — try again
        </p>
      );
    }
    return (
      <p className="text-xs" style={{ color: 'var(--color-danger)' }}>
        Couldn&apos;t send — try again
      </p>
    );
  }

  if (!onRequest) return null;

  const handleClick = (e) => {
    if (stopClick) { e.preventDefault(); e.stopPropagation(); }
    onRequest(speakerId);
  };

  if (prominent) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="text-sm font-bold px-4 py-2.5 rounded-xl transition-transform hover:scale-105"
        style={{
          background: 'var(--primary)',
          color: 'var(--on-primary)',
          border: 'none',
          boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
        }}
      >
        Ask {firstName} to be friends
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-opacity hover:opacity-80"
      style={{
        background: 'var(--bg-input)',
        border: '1px solid var(--border-subtle)',
        color: 'var(--primary)',
      }}
    >
      Ask {firstName} to be friends
    </button>
  );
}
